import {List} from 'immutable';
import {Observable} from 'rxjs';

import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as DraftQueries from 'web-app/react/entities/drafts/queries';
import * as DraftConstants from 'web-app/react/entities/drafts/constants';
import generateUUID from 'web-app/util/uuid';
import {collectFrameworkIdsFromObservations} from 'web-app/react/entities/observations/helpers';
import * as NewPostHelpers from 'web-app/react/feed/post/new-post/helpers';

import FeedItem from './model';
import * as Queries from './queries';

export const fetch = (olderThan, institutionId, heightTarget) => {
    // uuid is set to make sure that enterprise caches don't serve the content of other users
    return RESTClient.getAuthenticatedObservable('feed/feed/feed', {
        uuid: generateUUID(),
        olderThan,
        institutionId,
        heightTarget,
    }).map(response => List(response.feedItems).map(FeedItem.fromAPI));
};

export const fetchBookmarks = olderThan => {
    return RESTClient.getAuthenticatedObservable('feed/feed/bookmarks', {
        uuid: generateUUID(),
        olderThan,
    }).map(response => List(response.feedItems).map(FeedItem.fromAPI));
};

export const fetchObservations = observationIds => {
    return Observable.fromPromise(
        getApolloClient().query({
            fetchPolicy: 'network-only',
            query: Queries.ObservationsByIds,
            variables: {
                observationIds,
            },
        }),
    ).mergeMap(({data}) => {
        const frameworkIds = collectFrameworkIdsFromObservations(data.childDevelopment.observations.results);

        if (!frameworkIds || frameworkIds.length === 0) {
            return Observable.of({
                observations: data.childDevelopment.observations.results,
                frameworks: [],
            });
        }

        return Observable.fromPromise(
            getApolloClient().query({
                fetchPolicy: 'cache-first',
                query: Queries.FrameworksForObservations,
                variables: {
                    ids: frameworkIds,
                },
            }),
        ).map(response => ({
            observations: data.childDevelopment.observations.results,
            frameworks: response.data.childDevelopment.frameworks,
        }));
    });
};

export const fetchSingle = feedItemId =>
    RESTClient.getAuthenticatedObservable('feed/feed/showfeeditem', {
        feedItemId,
    }).map(response => ({
        feedItem: FeedItem.fromAPI({
            ...response.feedItem,
            /*
                We override the response with the originally requested id due to the following scenario
                1. User A has a link to a single feedItem and sends it to user B
                2. User B has permission to see the content of this post, but the feedItemId is different
                3. The backend returns the feedItem but under a different id.

                By always saving under the requested id we can still serve the requested data through
                our existing implementation.
             */
            feedItemId,
        }),
    }));

export const deletePost = originatorId => {
    return RESTClient.postAuthenticatedObservable('feed/feed/delete', {
        originatorId,
    });
};

export const deleteObservationPost = observationId => {
    return Observable.fromPromise(
        getApolloClient().mutate({
            mutation: DraftQueries.DeleteObservation,
            variables: {
                observationIds: [observationId],
            },
        }),
    );
};

export const like = originatorId => {
    return RESTClient.postAuthenticatedObservable('feed/feed/like', {
        originatorId,
    });
};

export const unlike = originatorId => {
    return RESTClient.postAuthenticatedObservable('feed/feed/unlike', {
        originatorId,
    });
};

export const comment = (originatorId, body, imageIds) => {
    return RESTClient.postAuthenticatedObservable('feed/feed/comment', {
        originatorId,
        body,
        imageIds,
    }).map(() => ({
        comment: FeedItem.commentFromAPI({
            commentId: 'new',
            body,
        }),
    }));
};

export const deleteComment = commentId => {
    return RESTClient.postAuthenticatedObservable('feed/feed/deletecomment', {
        commentId,
    });
};

export const likeComment = (commentId, like) => {
    if (like) {
        return RESTClient.postAuthenticatedObservable('feed/feed/likecomment', {
            commentId,
        });
    }
    return RESTClient.postAuthenticatedObservable('feed/feed/unlikecomment', {
        commentId,
    });
};

export const showCommentsAndLikes = originatorId => {
    return RESTClient.getAuthenticatedObservable('feed/feed/showcommentsandlikes', {
        originatorId,
    }).map(response => ({
        comments: List(response.comments).map(FeedItem.commentFromAPI),
        likes: List(response.likes).map(FeedItem.likeFromAPI),
    }));
};

export const updatePost = (
    originatorId,
    body,
    richTextBody,
    disableFeedback,
    notify,
    imageIds,
    fileIds,
    activities,
) => {
    const bodyArgument = richTextBody
        ? {richTextBody: NewPostHelpers.addActivitiesToBody(richTextBody, activities)}
        : {body: NewPostHelpers.addActivitiesToBody(body, activities)};
    return RESTClient.postAuthenticatedObservable('feed/feed/edit', {
        originatorId,
        ...bodyArgument,
        disableFeedback,
        notify,
        imageIds,
        fileIds,
    });
};

export const updateObservationPost = (
    observationId,
    childIds,
    from,
    body = '',
    nextStep = '',
    imageIds,
    videoIds,
    fileIds,
    statements = [],
    areaRefinements = [],
    observedAt,
    variant,
) => {
    return Observable.fromPromise(
        getApolloClient().mutate({
            mutation: DraftQueries.CreateObservation,
            variables: {
                observationId,
                childIds: childIds ? childIds.split(',') : [],
                remark: {
                    date: observedAt,
                    body,
                    statements,
                    areas: areaRefinements,
                },
                nextStep: {
                    body: nextStep,
                    statements: [],
                    areas: [],
                },
                imageIds,
                variant,
                videoId: videoIds[0], // TODO: Change when API accepts an array instead
                fileIds,
                status: DraftConstants.ObservationStatusState.SENT,
            },
        }),
    ).map(({data}) => ({
        observation: data.childDevelopment.observations.createObservation,
    }));
};

export const bookmarkPost = feedItemId => RESTClient.postAuthenticatedObservable(`v2/feed/${feedItemId}/bookmark`);

export const unBookmarkPost = feedItemId => RESTClient.deleteAuthenticatedObservable(`v2/feed/${feedItemId}/bookmark`);
